<template>
  <div class="indexed">
    <!-- The rail is the grid item and stretches to the row height; the nav is
         the sticky box inside it. Sticking the grid item itself does nothing,
         because it is only as tall as its own content. -->
    <div class="indexed__rail">
      <nav class="indexed__nav" :aria-label="label">
        <ul class="indexed__list">
          <li v-for="item in items" :key="item.id">
            <a
              class="indexed__link"
              :href="`#${item.id}`"
              :aria-current="item.id === activeId ? 'true' : undefined"
            >
              {{ item.label }}
            </a>
          </li>
        </ul>
      </nav>
    </div>
    <div class="indexed__body">
      <slot></slot>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

/*
 * Two-column reading layout: a sticky in-page index on the left, the content
 * on the right, collapsing to a single column with a wrapping chip row below
 * ~64rem.
 *
 * It exists because the reference pages were a narrow left-aligned column
 * under a centred title, floating in a wide dark background: the header and
 * the body disagreed about alignment and roughly a third of the desktop width
 * carried nothing. The index is the content that earns that width — the cheat
 * sheet already had one as a pill row that scrolled away on first swipe.
 *
 * Each slotted child is expected to carry an `id` matching an item.
 */
const props = defineProps({
  /* [{ id, label }] — id must match a slotted element's id. */
  items: { type: Array, required: true },
  label: { type: String, required: true },
});

const activeId = ref(props.items[0]?.id ?? '');
let observer = null;
const visible = new Set();

function connect() {
  disconnect();
  if (typeof IntersectionObserver === 'undefined') return;
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) visible.add(entry.target.id);
        else visible.delete(entry.target.id);
      });
      const first = props.items.find((item) => visible.has(item.id));
      if (first) activeId.value = first.id;
    },
    { rootMargin: '-15% 0px -70% 0px' },
  );
  props.items.forEach((item) => {
    const el = document.getElementById(item.id);
    if (el) observer.observe(el);
  });
}

function disconnect() {
  observer?.disconnect();
  observer = null;
  visible.clear();
}

onMounted(connect);
onBeforeUnmount(disconnect);
watch(() => props.items.map((item) => item.id).join(), connect);
</script>

<style scoped>
.indexed {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.indexed__body {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  min-width: 0;
}

.indexed__body > :slotted(*) {
  scroll-margin-top: var(--space-6);
}

.indexed__list {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  list-style: none;
  margin: 0;
  padding: 0;
}

.indexed__list > li {
  margin: 0;
}

.indexed__link {
  display: block;
  padding: 0.25rem 0.7rem;
  border: 1px solid var(--border-accent);
  border-radius: var(--radius-pill);
  background: var(--accent-wash);
  color: var(--accent);
  font-size: var(--text-sm);
  line-height: 1.4;
  text-decoration: none;
  /* Ukrainian compounds are long and must not be clipped. */
  overflow-wrap: anywhere;
  transition: color var(--duration) var(--ease), border-color var(--duration) var(--ease);
}

.indexed__link:hover,
.indexed__link:focus-visible {
  color: var(--accent-strong);
}

@media (min-width: 64rem) {
  .indexed {
    display: grid;
    grid-template-columns: minmax(0, 15rem) minmax(0, 1fr);
    gap: var(--space-8);
  }

  .indexed__nav {
    position: sticky;
    top: var(--space-5);
    max-height: calc(100vh - var(--space-8));
    overflow-y: auto;
  }

  .indexed__list {
    flex-direction: column;
    gap: 0;
    border-left: 1px solid var(--border);
  }

  .indexed__link {
    padding: var(--space-2) var(--space-4);
    border: 0;
    border-radius: 0;
    background: none;
    color: var(--text-muted);
    box-shadow: inset 2px 0 0 transparent;
  }

  .indexed__link:hover,
  .indexed__link:focus-visible {
    color: var(--accent-strong);
  }

  .indexed__link[aria-current='true'] {
    color: var(--accent);
    box-shadow: inset 2px 0 0 var(--accent);
  }
}

@media print {
  .indexed {
    display: block;
  }
}
</style>
